import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/app/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox, Input, Textarea, Label, Select } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Sheet, Muted, ConfirmDialog } from "@/components/ui/overlays";
import { useToast } from "@/app/Toast";
import { formatDateTime, formatMoney } from "@/lib/utils";

interface CustomerDetail {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  marketingOptIn: boolean;
  notesSummary: string | null;
  homeLocation: { id: number; name: string };
  vehicles: Vehicle[];
  notes: Array<{ id: number; body: string; createdAt: string; author: string }>;
  recentBookings: Array<{ id: number; code: string; status: string; startAt: string; endAt: string; total: { amount: number; currency: string } }>;
}

interface Vehicle {
  id: number;
  plate: string;
  make: string | null;
  model: string | null;
  color: string | null;
  vehicleType: string | null;
  notes: string | null;
  archivedAt: string | null;
  bookingCount: number;
}

export function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const canManage = hasRole("MANAGER", "ADMIN");
  const canDelete = hasRole("ADMIN");
  const [c, setC] = useState<CustomerDetail | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [vehicleEdit, setVehicleEdit] = useState<Vehicle | null | undefined>(undefined);
  const [vehicleAction, setVehicleAction] = useState<{ kind: "archive" | "delete"; vehicle: Vehicle } | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setC(await api.get<CustomerDetail>(`/api/v1/customers/${id}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load customer");
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!c) return <p className="text-sm text-muted-foreground">Loading…</p>;

  async function runVehicleAction() {
    if (!vehicleAction) return;
    setActionPending(true);
    try {
      if (vehicleAction.kind === "archive") {
        await api.post(`/api/v1/customers/${c!.id}/vehicles/${vehicleAction.vehicle.id}/archive`);
        toast("success", `${vehicleAction.vehicle.plate} archived`);
      } else {
        await api.delete(`/api/v1/customers/${c!.id}/vehicles/${vehicleAction.vehicle.id}`);
        toast("success", `${vehicleAction.vehicle.plate} deleted`);
      }
      await refresh();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Vehicle action failed");
    } finally {
      setActionPending(false);
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle>{c.firstName} {c.lastName}</CardTitle>
              {canManage && <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setCustomerEditOpen(true)}>Edit customer</Button>
                <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)}>Merge into this…</Button>
              </div>}
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" value={c.email} />
            <Field label="Phone" value={c.phone} />
            <Field label="Source" value={c.source} />
            <Field label="Home location" value={c.homeLocation.name} />
            <Field label="Marketing opt-in" value={c.marketingOptIn ? "Yes" : "No"} />
            <Field label="Summary" value={c.notesSummary} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Vehicles</CardTitle>
              {canManage && <Button size="sm" onClick={() => setVehicleEdit(null)}>Add vehicle</Button>}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plate</TableHead>
                  <TableHead>Make</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.vehicles.map((v) => (
                  <TableRow key={v.id} className={v.archivedAt ? "bg-muted/30 text-muted-foreground" : undefined}>
                    <TableCell className="font-mono">{v.plate}</TableCell>
                    <TableCell>{v.make ?? "—"}</TableCell>
                    <TableCell>{v.model ?? "—"}</TableCell>
                    <TableCell>{v.color ?? "—"}</TableCell>
                    <TableCell>{v.vehicleType ?? "—"}</TableCell>
                    <TableCell>
                      {v.archivedAt ? <Badge className="border-muted-foreground/20 bg-muted text-muted-foreground">Archived</Badge> : <Badge>Active</Badge>}
                    </TableCell>
                    {canManage && <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setVehicleEdit(v)}>Edit</Button>
                        {!v.archivedAt && <Button size="sm" variant="outline" onClick={() => setVehicleAction({ kind: "archive", vehicle: v })}>Archive</Button>}
                        {canDelete && v.bookingCount === 0 && <Button size="sm" variant="destructive" onClick={() => setVehicleAction({ kind: "delete", vehicle: v })}>Delete</Button>}
                      </div>
                    </TableCell>}
                  </TableRow>
                ))}
                {c.vehicles.length === 0 && (
                  <TableRow><TableCell colSpan={canManage ? 7 : 6} className="text-muted-foreground">No vehicles on file.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Booking history</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.recentBookings.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono">{b.code}</TableCell>
                    <TableCell><Badge>{b.status}</Badge></TableCell>
                    <TableCell>{formatDateTime(b.startAt)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(b.total.amount, b.total.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Add note</CardTitle></CardHeader>
          <CardContent>
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post(`/api/v1/customers/${id}/notes`, { body: note });
                  setNote("");
                  toast("success", "Note saved");
                  await refresh();
                } catch (err) {
                  toast("error", err instanceof Error ? err.message : "Could not save note");
                }
              }}
            >
              <Label htmlFor="note" className="sr-only">Note</Label>
              <Textarea id="note" required value={note} onChange={(e) => setNote(e.target.value)} />
              <Button type="submit" disabled={!note.trim()}>Save note</Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Recent notes</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {c.notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
            {c.notes.map((n) => (
              <div key={n.id} className="rounded-md border bg-muted/40 p-3 text-sm">
                <p>{n.body}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {n.author} · {formatDateTime(n.createdAt)}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <MergeSheet
        open={mergeOpen}
        targetId={c.id}
        targetName={`${c.firstName} ${c.lastName}`}
        onClose={() => setMergeOpen(false)}
        onMerged={() => { setMergeOpen(false); nav("/customers"); }}
      />
      <CustomerEditSheet
        open={customerEditOpen}
        customer={c}
        onClose={() => setCustomerEditOpen(false)}
        onSaved={async () => { setCustomerEditOpen(false); await refresh(); }}
      />
      <VehicleEditSheet
        open={vehicleEdit !== undefined}
        customerId={c.id}
        vehicle={vehicleEdit ?? null}
        onClose={() => setVehicleEdit(undefined)}
        onSaved={async () => { setVehicleEdit(undefined); await refresh(); }}
      />
      <ConfirmDialog
        open={vehicleAction != null}
        onClose={() => { if (!actionPending) setVehicleAction(null); }}
        title={vehicleAction?.kind === "delete" ? `Delete ${vehicleAction.vehicle.plate}?` : `Archive ${vehicleAction?.vehicle.plate ?? "vehicle"}?`}
        description={vehicleAction?.kind === "delete"
          ? "This permanently deletes the vehicle. Deletion is only allowed when it has never been used on a booking."
          : "The vehicle will no longer be active, but existing booking history will keep its vehicle association."}
        confirmLabel={actionPending ? "Working…" : vehicleAction?.kind === "delete" ? "Delete vehicle" : "Archive vehicle"}
        destructive
        onConfirm={runVehicleAction}
      />
    </div>
  );
}

function CustomerEditSheet({ open, customer, onClose, onSaved }: {
  open: boolean;
  customer: CustomerDetail;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => customerForm(customer));
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => { if (open) setForm(customerForm(customer)); }, [open, customer]);
  useEffect(() => {
    if (!open) return;
    api.get<Array<{ id: number; name: string }>>("/api/v1/locations").then((rows) => {
      setLocations(user?.role === "ADMIN" ? rows : rows.filter((location) => user?.memberships.some((membership) => membership.locationId === location.id && membership.scope === "MANAGE")));
    }).catch(() => setLocations([]));
  }, [open, user]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/api/v1/customers/${customer.id}`, {
        firstName: form.firstName.trim(),
        homeLocationId: form.homeLocationId,
        lastName: form.lastName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        source: form.source.trim() || null,
        marketingOptIn: form.marketingOptIn,
        notesSummary: form.notesSummary.trim() || null,
      });
      toast("success", "Customer updated");
      await onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Customer update failed");
    } finally {
      setSaving(false);
    }
  }

  return <Sheet open={open} onClose={onClose} title="Edit customer" description="Update identity, contact, acquisition source, consent, and CRM summary.">
    <form className="space-y-4" onSubmit={save}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput label="First name" required value={form.firstName} onChange={(firstName) => setForm({ ...form, firstName })} />
        <FormInput label="Last name" required value={form.lastName} onChange={(lastName) => setForm({ ...form, lastName })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="customer-home-location">Home location</Label>
        <Select id="customer-home-location" required value={form.homeLocationId} onChange={(event) => setForm({ ...form, homeLocationId: Number(event.target.value) })}>
          {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
        </Select>
      </div>
      <FormInput label="Email" type="email" value={form.email} onChange={(email) => setForm({ ...form, email })} />
      <FormInput label="Phone" type="tel" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
      <FormInput label="Source" value={form.source} onChange={(source) => setForm({ ...form, source })} placeholder="Referral, walk-in, campaign…" />
      <div className="space-y-2">
        <Label htmlFor="customer-summary">Summary</Label>
        <Textarea id="customer-summary" maxLength={500} value={form.notesSummary} onChange={(e) => setForm({ ...form, notesSummary: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={form.marketingOptIn} onChange={(e) => setForm({ ...form, marketingOptIn: e.target.checked })} />
        Customer consents to marketing messages
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </form>
  </Sheet>;
}

function VehicleEditSheet({ open, customerId, vehicle, onClose, onSaved }: {
  open: boolean;
  customerId: number;
  vehicle: Vehicle | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => vehicleForm(vehicle));
  useEffect(() => { if (open) setForm(vehicleForm(vehicle)); }, [open, vehicle]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const body = {
      plate: form.plate.trim(),
      make: form.make.trim() || null,
      model: form.model.trim() || null,
      color: form.color.trim() || null,
      vehicleType: form.vehicleType.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (vehicle) await api.patch(`/api/v1/customers/${customerId}/vehicles/${vehicle.id}`, body);
      else await api.post(`/api/v1/customers/${customerId}/vehicles`, body);
      toast("success", vehicle ? "Vehicle updated" : "Vehicle added");
      await onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Vehicle save failed");
    } finally {
      setSaving(false);
    }
  }

  return <Sheet open={open} onClose={onClose} title={vehicle ? "Edit vehicle" : "Add vehicle"} description={vehicle?.archivedAt ? "This vehicle is archived. Changes do not reactivate it." : undefined}>
    <form className="space-y-4" onSubmit={save}>
      <FormInput label="Plate" required maxLength={32} value={form.plate} onChange={(plate) => setForm({ ...form, plate })} />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput label="Make" maxLength={80} value={form.make} onChange={(make) => setForm({ ...form, make })} />
        <FormInput label="Model" maxLength={80} value={form.model} onChange={(model) => setForm({ ...form, model })} />
        <FormInput label="Color" maxLength={40} value={form.color} onChange={(color) => setForm({ ...form, color })} />
        <FormInput label="Type" maxLength={40} value={form.vehicleType} onChange={(vehicleType) => setForm({ ...form, vehicleType })} placeholder="Sedan, SUV, van…" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="vehicle-notes">Vehicle notes</Label>
        <Textarea id="vehicle-notes" maxLength={500} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving || !form.plate.trim()}>{saving ? "Saving…" : vehicle ? "Save changes" : "Add vehicle"}</Button>
      </div>
    </form>
  </Sheet>;
}

function FormInput({ label, onChange, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> & { label: string; onChange: (value: string) => void }) {
  const fieldId = `field-${label.toLowerCase().replaceAll(" ", "-")}`;
  return <div className="space-y-2">
    <Label htmlFor={fieldId}>{label}</Label>
    <Input id={fieldId} onChange={(e) => onChange(e.target.value)} {...props} />
  </div>;
}

function customerForm(customer: CustomerDetail) {
  return {
    firstName: customer.firstName,
    homeLocationId: customer.homeLocation.id,
    lastName: customer.lastName,
    email: customer.email ?? "",
    phone: customer.phone ?? "",
    source: customer.source ?? "",
    marketingOptIn: customer.marketingOptIn,
    notesSummary: customer.notesSummary ?? "",
  };
}

function vehicleForm(vehicle: Vehicle | null) {
  return {
    plate: vehicle?.plate ?? "",
    make: vehicle?.make ?? "",
    model: vehicle?.model ?? "",
    color: vehicle?.color ?? "",
    vehicleType: vehicle?.vehicleType ?? "",
    notes: vehicle?.notes ?? "",
  };
}

function MergeSheet({
  open, targetId, targetName, onClose, onMerged,
}: {
  open: boolean;
  targetId: number;
  targetName: string;
  onClose: () => void;
  onMerged: () => void;
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Array<{ id: number; firstName: string; lastName: string; email: string | null; phone: string | null }>>([]);
  const [selected, setSelected] = useState<{ id: number; firstName: string; lastName: string; email: string | null; phone: string | null } | null>(null);
  const [merging, setMerging] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); setSelected(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await api.get<Array<{ id: number; firstName: string; lastName: string; email: string | null; phone: string | null }>>(
          `/api/v1/customers?q=${encodeURIComponent(query)}&limit=20`,
        );
        setResults(data.filter((r) => r.id !== targetId));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open, targetId]);

  async function doMerge() {
    if (!selected) return;
    setMerging(true);
    try {
      await api.post(`/api/v1/customers/merge`, { sourceCustomerId: selected.id, targetCustomerId: targetId });
      toast("success", `Merged ${selected.firstName} ${selected.lastName} into ${targetName}`);
      onMerged();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Merge failed");
    } finally {
      setMerging(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Merge another customer into this one" description="All bookings, vehicles, and notes from the source will be reassigned. The source is then soft-deleted. This action is logged to the audit trail.">
      <div className="space-y-3">
        <div>
          <Label>Search for the duplicate</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="name, phone, or email" autoFocus />
        </div>
        <div className="max-h-72 overflow-y-auto rounded border">
          {searching && <p className="p-3 text-sm text-muted-foreground">Searching…</p>}
          {!searching && results.length === 0 && <p className="p-3 text-sm text-muted-foreground">No matches.</p>}
          {results.map((r) => (
            <label key={r.id} className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/30">
              <input
                type="radio"
                name="merge-source"
                checked={selected?.id === r.id}
                onChange={() => setSelected(r)}
              />
              <div className="flex-1">
                <p className="text-sm font-medium">{r.firstName} {r.lastName}</p>
                <p className="text-xs text-muted-foreground">
                  {r.email ?? "—"}{r.phone ? ` · ${r.phone}` : ""}
                </p>
              </div>
            </label>
          ))}
        </div>
        <ConfirmDialog
          open={selected != null}
          onClose={() => setSelected(null)}
          title={`Merge ${selected ? `${selected.firstName} ${selected.lastName}` : ""} into ${targetName}?`}
          description="All bookings, vehicles, notes, and payments from the source are moved to this customer. The source is then soft-deleted (marked inactive). This is irreversible from the UI; contact an admin to recover."
          confirmLabel="Merge"
          destructive
          onConfirm={doMerge}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
        {merging && <Muted>Merging…</Muted>}
      </div>
    </Sheet>
  );
}

const Field: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm">{value ?? "—"}</p>
  </div>
);
